import type { InsightCardItem } from '@wix/ai-scheduling-assistant-common-lib/entities';

const KNOWN_ROLES = new Set(['sales', 'bookings', 'reviews', 'marketing', 'finance', 'support']);
/** Kinds whose window looks forward ("Next 7 days"); to confirm with symphony-packs-service (plan, Contract requests 3). */
const FORWARD_WINDOW_KINDS = new Set(['planRenewals', 'sessions']);
/** Kinds whose copy has a `.detail` line (plan, "Copy"). The line needs `count`. */
const DETAIL_KINDS = new Set(['carts', 'abandonedBookings', 'unpaidBookings', 'overdueInvoices', 'planRenewals', 'forms', 'productSales']);

const formatUnitValue = (item: InsightCardItem, locale: string): string => {
  if (item.unit === 'percent') {
    return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(item.value / 100);
  }
  if (item.unit === 'money' && item.currency) {
    // Both fraction bounds: older Safari throws a RangeError on a max below the currency's default min.
    return new Intl.NumberFormat(locale, { style: 'currency', currency: item.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(
      item.value,
    );
  }
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(item.value);
};

/**
 * The tile's big number, by the one rule all three surfaces share (plan, "Value format"). `capped`
 * belongs to `count` when there is one, so the `+` goes on the big number only without a count, and
 * after the unit format, so a capped money or percent value keeps its lower-bound marker too.
 */
export const formatInsightValue = (item: InsightCardItem, locale: string): string => {
  if (item.kind === 'gbpConnection') {
    return '';
  }
  const value = formatUnitValue(item, locale);
  return item.capped && item.count === undefined ? `${value}+` : value;
};

/**
 * The card's "As of" date, in UTC: the date the tool result and the accept note quote
 * (`computedAt.slice(0, 10)`), so the card and the reply never name different days near midnight.
 */
export const formatInsightAsOf = (computedAt: string, locale: string): string =>
  new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(computedAt));

/** The `{count}` of the detail line: locale digits, with `+` when the count hit the read's cap. */
export const formatInsightCount = (item: InsightCardItem, locale: string): string | undefined => {
  if (item.count === undefined) {
    return undefined;
  }
  const number = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(item.count);
  return item.capped ? `${number}+` : number;
};

export const insightWindowKey = (item: InsightCardItem): string | undefined => {
  if (item.windowDays === undefined) {
    return undefined;
  }
  return FORWARD_WINDOW_KINDS.has(item.kind) ? `chat.insightCard.kind.${item.kind}.window` : 'chat.insightCard.window';
};

/** The repo's plural convention: a `.one` / `.other` pair picked by hand (see `brief.summaryAgentsMet`). */
export const insightDetailKey = (item: InsightCardItem): string | undefined =>
  item.count !== undefined && DETAIL_KINDS.has(item.kind)
    ? `chat.insightCard.kind.${item.kind}.detail.${item.count === 1 ? 'one' : 'other'}`
    : undefined;

export const insightRoleKey = (role: string): string => `chat.insightCard.role.${KNOWN_ROLES.has(role) ? role : 'other'}`;
